import React, { useRef, useEffect } from 'react';

const FormatStep = ({ formData, handleOptionSelect }) => {
    // Set default size and frame if not already set
    useEffect(() => {
        if (!formData.size) {
            handleOptionSelect('size', '12×16 in / 30×40 cm');
        }

        if (!formData.frame) {
            handleOptionSelect('frame', 'wood');
        }

        // Set default background to golden-black if not already set
        if (!formData.background) {
            handleOptionSelect('background', 'golden-black');
        }
    }, []);

    // Create refs for the dropdown elements
    const sizeDropdownButtonRef = useRef(null);
    const sizeDropdownMenuRef = useRef(null);
    const frameDropdownButtonRef = useRef(null);
    const frameDropdownMenuRef = useRef(null);

    // Effect for handling clicks outside the dropdown
    useEffect(() => {
        function handleClickOutside(event) {
            // Close the size dropdown if it's open and the click is outside
            if (formData.sizeDropdownOpen &&
                sizeDropdownButtonRef.current &&
                sizeDropdownMenuRef.current &&
                !sizeDropdownButtonRef.current.contains(event.target) &&
                !sizeDropdownMenuRef.current.contains(event.target)) {
                handleOptionSelect('sizeDropdownOpen', false);
            }

            // Close the frame dropdown if it's open and the click is outside
            if (formData.frameDropdownOpen &&
                frameDropdownButtonRef.current &&
                frameDropdownMenuRef.current &&
                !frameDropdownButtonRef.current.contains(event.target) &&
                !frameDropdownMenuRef.current.contains(event.target)) {
                handleOptionSelect('frameDropdownOpen', false);
            }
        }

        // Add event listener to document
        document.addEventListener("mousedown", handleClickOutside);

        // Clean up the event listener
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [formData.sizeDropdownOpen, formData.frameDropdownOpen, handleOptionSelect]);

    // Get price based on selected size
    const getSizePrice = () => {
        switch (formData.size) {
            case '8×10 in / 20×25 cm':
                return '€ 5.00';
            case '24×36 in / 60×90 cm':
                return '€ 15.00';
            case '12×16 in / 30×40 cm':
            default:
                return '€ 10.00';
        }
    };

    // Set default format to 'digital' on component mount
    useEffect(() => {
        if (!formData.format) {
            handleOptionSelect('format', 'digital');
        }
    }, []);

    return (
        <div className="space-y-6">
            <div className="-mt-4">
                <h3 className="text-[31px] text-center md:text-left leading-tight font-light mb-4 mt-6 md:mt-0">Choose Your Poster Background</h3>
                <p className="mb-8 text-gray-300 text-[16px] md:text-[20px] text-center md:text-left">
                    Optional: Select a background that complements your interior or sets the mood for your occasion. Choose from classic black and white to warm or vibrant accents.
                </p>

                <div className="flex flex-col gap-y-8">
                    <div className="grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                        <div className="flex flex-col">
                            <div className="flex flex-col md:flex-row justify-between md:items-end">
                                <div className="flex flex-col">
                                    <div className="text-[14px] text-[#A0A0A0]">
                                        Basic Harmony
                                    </div>
                                    <div className="text-[20px] mt-0">
                                        € 5.00
                                    </div>
                                </div>
                                <div className="flex gap-3 mt-2 md:mt-0">
                                    <button
                                        className={`w-[30px] h-[30px] rounded-full ${formData.background === 'basic-light' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                        onClick={() => handleOptionSelect('background', 'basic-light')}
                                    >
                                        <img src="/images/icon-gradients/gradient-1.svg" className="w-full h-full object-cover" />
                                    </button>
                                    <button
                                        className={`w-[30px] h-[30px] rounded-full ${formData.background === 'basic-dark' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                        onClick={() => handleOptionSelect('background', 'basic-dark')}
                                    >
                                        <img src="/images/icon-gradients/gradient-2.svg" className="w-full h-full object-cover" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col">
                            <div className="flex flex-col md:flex-row justify-between md:items-end">
                                <div className="flex flex-col">
                                    <div className="text-[14px] text-[#A0A0A0]">
                                        Sunset Vibe
                                    </div>
                                    <div className="text-[20px] mt-0">
                                        € 8.80
                                    </div>
                                </div>
                                <div className="flex gap-3 mt-2 md:mt-0">
                                    <button
                                        className={`w-[30px] h-[30px] rounded-full ${formData.background === 'sunset-pink' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                        onClick={() => handleOptionSelect('background', 'sunset-pink')}
                                    >
                                        <img src="/images/icon-gradients/gradient-9.svg" className="w-full h-full object-cover" />
                                    </button>
                                    <button
                                        className={`w-[30px] h-[30px] rounded-full ${formData.background === 'sunset-orange' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                        onClick={() => handleOptionSelect('background', 'sunset-orange')}
                                    >
                                        <img src="/images/icon-gradients/gradient-10.svg" className="w-full h-full object-cover" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                        <div className="flex flex-col">
                            <div className="flex flex-col md:flex-row justify-between md:items-end">
                                <div className="flex flex-col">
                                    <div className="text-[14px] text-[#A0A0A0]">
                                        Pastel Whispers
                                    </div>
                                    <div className="text-[20px] mt-0">
                                        € 6.50
                                    </div>
                                </div>
                                <div className="flex gap-3 mt-2 md:mt-0">
                                    <button
                                        className={`w-[30px] h-[30px] rounded-full ${formData.background === 'pastel-pink' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                        onClick={() => handleOptionSelect('background', 'pastel-pink')}
                                    >
                                        <img src="/images/icon-gradients/gradient-3.svg" className="w-full h-full object-cover" />
                                    </button>
                                    <button
                                        className={`w-[30px] h-[30px] rounded-full ${formData.background === 'pastel-blue' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                        onClick={() => handleOptionSelect('background', 'pastel-blue')}
                                    >
                                        <img src="/images/icon-gradients/gradient-4.svg" className="w-full h-full object-cover" />
                                    </button>
                                    <button
                                        className={`w-[30px] h-[30px] rounded-full ${formData.background === 'pastel-white' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                        onClick={() => handleOptionSelect('background', 'pastel-white')}
                                    >
                                        <img src="/images/icon-gradients/gradient-5.svg" className="w-full h-full object-cover" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col">
                            <div className="flex flex-col md:flex-row justify-between md:items-end">
                                <div className="flex flex-col">
                                    <div className="text-[14px] text-[#A0A0A0]">
                                        Neon Pop
                                    </div>
                                    <div className="text-[20px] mt-0">
                                        € 9.90
                                    </div>
                                </div>
                                <div className="flex gap-3 mt-2 md:mt-0">
                                    <button
                                        className={`w-[30px] h-[30px] rounded-full relative ${formData.background === 'neon-purple' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800 z-10' : ''}`}
                                        style={{
                                            background: "radial-gradient(circle, rgba(192,38,211,1) 0%, rgba(253,29,191,0.7) 40%, rgba(69,10,94,1) 100%)"
                                        }}
                                        onClick={() => handleOptionSelect('background', 'neon-purple')}
                                    >
                                        <img src="/images/icon-gradients/gradient-11.png" className="w-full h-full object-cover" />
                                    </button>
                                    <button
                                        className={`w-[30px] h-[30px] rounded-full relative ${formData.background === 'neon-blue' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800 z-10' : ''}`}
                                        style={{
                                            background: "radial-gradient(circle, rgba(59,130,246,1) 0%, rgba(37,99,235,1) 40%, rgba(30,58,138,1) 100%)"
                                        }}
                                        onClick={() => handleOptionSelect('background', 'neon-blue')}
                                    >
                                        <img src="/images/icon-gradients/gradient-12.png" className="w-full h-full object-cover" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                        <div className="flex flex-col">
                            <div className="flex flex-col md:flex-row justify-between md:items-end">
                                <div className="flex flex-col">
                                    <div className="text-[14px] text-[#A0A0A0]">
                                        Natural Soul
                                    </div>
                                    <div className="text-[20px] mt-0">
                                        € 7.20
                                    </div>
                                </div>
                                <div className="flex gap-3 mt-2 md:mt-0">
                                    <button
                                        className={`w-[30px] h-[30px] rounded-full ${formData.background === 'natural-gold' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                        onClick={() => handleOptionSelect('background', 'natural-gold')}
                                    >
                                        <img src="/images/icon-gradients/gradient-6.svg" className="w-full h-full object-cover" />
                                    </button>
                                    <button
                                        className={`w-[30px] h-[30px] rounded-full ${formData.background === 'natural-brown' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                        onClick={() => handleOptionSelect('background', 'natural-brown')}
                                    >
                                        <img src="/images/icon-gradients/gradient-7.svg" className="w-full h-full object-cover" />
                                    </button>
                                    <button
                                        className={`w-[30px] h-[30px] rounded-full ${formData.background === 'natural-beige' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                        onClick={() => handleOptionSelect('background', 'natural-beige')}
                                    >
                                        <img src="/images/icon-gradients/gradient-8.svg" className="w-full h-full object-cover" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col">
                            <div className="flex flex-col md:flex-row justify-between md:items-end">
                                <div className="flex flex-col">
                                    <div className="text-[14px] text-[#A0A0A0]">
                                        Golden Elegance
                                    </div>
                                    <div className="text-[20px] mt-0">
                                        € 11.00
                                    </div>
                                </div>
                                <div className="flex gap-3 mt-2 md:mt-0">
                                    <button
                                        className={`w-[30px] h-[30px] rounded-full relative ${formData.background === 'golden-dark' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800 z-10' : ''}`}
                                        style={{
                                            background: "radial-gradient(circle, rgba(180,83,9,1) 0%, rgba(66,30,15,1) 60%, rgba(0,0,0,1) 100%)"
                                        }}
                                        onClick={() => handleOptionSelect('background', 'golden-dark')}
                                    >
                                        <img src="/images/icon-gradients/gradient-13.png" className="w-full h-full object-cover" />
                                    </button>
                                    <button
                                        className={`w-[30px] h-[30px] rounded-full relative ${formData.background === 'golden-black' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800 z-10' : ''}`}
                                        style={{
                                            background: "radial-gradient(circle, rgba(75,85,99,1) 0%, rgba(31,41,55,1) 50%, rgba(17,24,39,1) 80%, rgba(0,0,0,1) 100%)"
                                        }}
                                        onClick={() => handleOptionSelect('background', 'golden-black')}
                                    >
                                        <img src="/images/icon-gradients/gradient-14.png" className="w-full h-full object-cover" />
                                    </button>
                                    <button
                                        className={`w-[30px] h-[30px] rounded-full relative ${formData.background === 'golden-gold' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800 z-10' : ''}`}
                                        style={{
                                            background: "radial-gradient(circle, rgba(252,211,77,1) 0%, rgba(217,119,6,1) 60%, rgba(120,53,15,1) 100%)"
                                        }}
                                        onClick={() => handleOptionSelect('background', 'golden-gold')}
                                    >
                                        <img src="/images/icon-gradients/gradient-15.png" className="w-full h-full object-cover" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="border-t border-dotted border-[#A0A0A0] mt-10 mb-8"></div>
            </div>

            <div>
                <h3 className="font-light mb-6 text-[20px]">Choose Your Format & Size</h3>
                <div className="flex flex-col md:flex-row gap-4">
                    {[
                        {
                            id: 'digital',
                            label: 'Digital Download (PDF)',
                            description: 'High-resolution file delivered instantly by email — print it anytime, anywhere. Perfect for last-minute gifts!'
                        },
                        {
                            id: 'printed',
                            label: 'Printed Poster',
                            description: 'Premium-quality print shipped to your door — ready to frame. Free worldwide shipping included.'
                        }
                    ].map((option) => (
                        <div
                            key={option.id}
                            className={`relative cursor-pointer rounded-[30px] p-6 text-center border flex-1 ${formData.format === option.id ? 'border-white bg-[#181819]' : 'border-[#181819] bg-[#181819]/60'}`}
                            onClick={() => handleOptionSelect('format', option.id)}
                        >
                            <div className="font-light mb-2 text-[20px]">{option.label}</div>
                            <div className="text-[14px] text-gray-[#A0A0A0]">{option.description}</div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-8">
                <div className="flex mb-2">
                    <div className="flex-1 text-[14px] font-normal">Size</div>
                    <div className="w-[100px] text-[14px] font-normal">Price</div>
                </div>
                <div className="flex gap-4">
                    <div className="flex-1 relative">
                        <div
                            className="rounded-lg border border-white py-2 px-4 flex justify-between items-center w-full cursor-pointer relative z-10"
                            onClick={() => handleOptionSelect('sizeDropdownOpen', !formData.sizeDropdownOpen)}
                            ref={sizeDropdownButtonRef}
                        >
                            <div className="text-[14px] font-light">
                                {formData.size || '12×16 in / 30×40 cm'}
                            </div>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                        </div>
                        {formData.sizeDropdownOpen && (
                            <div
                                className="absolute top-[calc(100%_+_5px)] left-0 right-0 bg-black rounded-lg border border-white shadow-lg w-full min-w-[280px] z-20 overflow-hidden"
                                ref={sizeDropdownMenuRef}
                                style={{ width: 'calc(100% + 80px)' }}
                            >
                                {[
                                    { size: '8×10 in / 20×25 cm', price: '€ 5.00' },
                                    { size: '12×16 in / 30×40 cm', price: '€ 10.00' },
                                    { size: '24×36 in / 60×90 cm', price: '€ 15.00' }
                                ].map((option) => (
                                    <div
                                        key={option.size}
                                        className="px-4 py-2.5 hover:bg-[#181819] cursor-pointer text-gray-200 text-[14px] flex justify-between items-center"
                                        onClick={() => {
                                            handleOptionSelect('size', option.size);
                                            handleOptionSelect('sizeDropdownOpen', false);
                                        }}
                                    >
                                        <span className="mr-8 whitespace-nowrap">{option.size}</span>
                                        <span className="text-gray-400 text-[14px] whitespace-nowrap">{option.price}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-start w-[100px]">
                        <div className="text-[14px] font-light">
                            {getSizePrice()}
                        </div>
                    </div>
                </div>
            </div>

            <div className="border-t border-dotted border-[#A0A0A0] !mt-10 mb-8"></div>

            <div>
                <h3 className="font-light mb-6 text-[20px]">Frame Style</h3>
                <p className="mb-8 text-gray-300 text-[14px]">
                    Complete your star map with the perfect frame — from sleek modern to classic wood finishes, designed to match your space and make your moment shine.
                </p>

                <div className="flex mb-2">
                    <div className="flex-1 text-[14px] font-normal">Name</div>
                    <div className="w-[100px] text-[14px] font-normal">Price</div>
                </div>
                <div className="flex gap-4">
                    <div className="flex-1 relative">
                        <div
                            className="rounded-lg border border-white py-2 px-4 flex justify-between items-center w-full cursor-pointer relative z-10"
                            onClick={() => handleOptionSelect('frameDropdownOpen', !formData.frameDropdownOpen)}
                            ref={frameDropdownButtonRef}
                        >
                            <div className="flex items-center text-[14px] font-light">
                                {formData.frame === 'wood' && (
                                    <div className="w-[24px] h-[24px] rounded-full bg-[#B57254] mr-2"></div>
                                )}
                                {formData.frame === 'black' && (
                                    <div className="w-[24px] h-[24px] rounded-full bg-[#222222] mr-2"></div>
                                )}
                                {formData.frame === 'metal' && (
                                    <div className="w-[24px] h-[24px] rounded-full bg-[#A8A8A8] mr-2"></div>
                                )}
                                {formData.frame === 'none' && (
                                    <div className="w-[24px] h-[24px] rounded-full border border-gray-400 mr-2"></div>
                                )}
                                {formData.frame === 'wood' ? 'Oak Wooden' :
                                    formData.frame === 'black' ? 'Classic Black' :
                                        formData.frame === 'metal' ? 'Silver Metal' : 'No Frame'}
                            </div>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                        </div>
                        {formData.frameDropdownOpen && (
                            <div
                                className="absolute top-[calc(100%_+_5px)] left-0 right-0 bg-black rounded-lg border border-white shadow-lg w-full min-w-[280px] z-20 overflow-hidden"
                                ref={frameDropdownMenuRef}
                                style={{ width: 'calc(100% + 80px)' }}
                            >
                                {[
                                    { id: 'none', label: 'No Frame', price: '€ 0', color: 'transparent' },
                                    { id: 'wood', label: 'Oak Wooden', price: '€ 7.50', color: '#B57254' },
                                    { id: 'black', label: 'Classic Black', price: '€ 6.00', color: '#222222' },
                                    { id: 'metal', label: 'Silver Metal', price: '€ 9.00', color: '#A8A8A8' }
                                ].map((option) => (
                                    <div
                                        key={option.id}
                                        className="px-4 py-2.5 hover:bg-[#181819] cursor-pointer text-gray-200 text-[14px] flex justify-between items-center"
                                        onClick={() => {
                                            handleOptionSelect('frame', option.id);
                                            handleOptionSelect('frameDropdownOpen', false);
                                        }}
                                    >
                                        <span className="flex items-center mr-8 whitespace-nowrap">
                                            <div
                                                className={`w-[22px] h-[22px] rounded-full mr-2 ${option.id === 'none' ? 'border border-gray-400' : ''}`}
                                                style={{ backgroundColor: option.color }}
                                            ></div>
                                            {option.label}
                                        </span>
                                        <span className="text-gray-400 text-[14px] whitespace-nowrap">{option.price}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-start w-[100px]">
                        <div className="text-[14px] font-light">
                            {formData.frame === 'wood' ? '€ 7.50' :
                                formData.frame === 'black' ? '€ 6.00' :
                                    formData.frame === 'metal' ? '€ 9.00' : '€ 0'}
                        </div>
                    </div>

                </div>

                <div className="border-t border-dotted border-[#A0A0A0] !mt-10"></div>

            </div>
        </div>
    );
};

export default FormatStep; 